import { Box, Button, Paper, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { StepperDataContext } from './AddEditCustomer'
import { Controller, useForm } from 'react-hook-form'
import { MuiOtpInput } from 'mui-one-time-password-input';
import useResponsive from '../../../hooks/useResponsive';
import Swal from 'sweetalert2';
import { CustomerService } from '../Service';
import { PermissionGuard } from '../../../components/PermissionGuard';

const OtpVerification = (props) => {
  const { setStep,otpExpirationTime,verifyOtp } = props;
  const { otrDetails,setTabValue,fetchCustomerDetailById,customerData } = useContext(StepperDataContext);
  const { isSm } = useResponsive();
  let otpBoxSize = isSm ? "100%" : "60%";

  // React Hook Form setup
  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm({
    defaultValues: {
      otp: ''
    },
    mode: 'onChange'
  });

  const watchedOtp = watch('otp');
  const [resendTimer, setResendTimer] = useState(120); // Start with 2 minutes
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const [isVerifying,setIsVerifying] = useState(false);

  // Calculate initial timer based on expiration time
  useEffect(() => {
    if (otpExpirationTime) {
      const calculateRemainingTime = () => {
        const now = new Date().getTime();
        const expirationTime = new Date(otpExpirationTime).getTime();
        const remainingTime = Math.max(0, Math.floor((expirationTime - now) / 1000));
        
        setResendTimer(remainingTime);
        
        // If already expired
        if (remainingTime <= 0) {
          setIsOtpExpired(true);
        }
      };

      calculateRemainingTime();
      
      // Update every second to account for any time drift
      const syncInterval = setInterval(calculateRemainingTime, 1000);
      
      return () => clearInterval(syncInterval);
    } else {
      // Fallback to 120 seconds if no expiration time provided
      setResendTimer(120);
    }
  }, [otpExpirationTime]);
  // Timer effect for resend OTP countdown
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(timer => {
          if (timer <= 1) {
            setIsOtpExpired(true);
            return 0;
          }
          return timer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Format time display (MM:SS)
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get color based on remaining time
  const getTimerColor = (seconds) => {
    if (seconds > 60) return '#4caf50'; // Green
    if (seconds > 30) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  const handleOtpChange = (otpValue) => {
    console.log("otpValue",otpValue)
    setValue('otp', otpValue, { shouldValidate: true });
  };

  const handleOtpResend = async () => {
    try {
      setIsLoading(true);
      
      // Clear current OTP and reset expired state
      setValue('otp', '');
      setIsOtpExpired(false);
      
      // Add your OTP resend API call here
      // const mobile = otrDetails?.mobileNumber.replace(/\D/g, '');
      // await sendOtp({ mobileNumber: mobile });
      
      // Reset timer
      setResendTimer(120);
      
      // Show success message
      // toast.success('OTP Resent Successfully');
      
      console.log('OTP Resend requested');
      
    } catch (error) {
      console.error('Failed to resend OTP:', error);
      // toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data) => {
    // Check if OTP is expired before submitting
    if (isOtpExpired) {
      console.log('Cannot verify expired OTP');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'OTP has expired. Please request a new one.',
      })
      // toast.error('OTP has expired. Please request a new one.');
      return;
    }

    try {
      setIsVerifying(true);
      console.log('OTP Verification Data:', data);
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, Verify!',
        allowOutsideClick: false
      })
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Processing...',
          text: 'Please wait while verifying the otp',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        })
        const otpVerificationResponse = await CustomerService.verifyMobileNoOtp({
          mobileNumber: otrDetails?.mobileNumber?.replace(/\D/g, ''),
          otp: Number(data.otp),
        });
        if (otpVerificationResponse.success == '1') {
          setIsLoading(false);
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: otpVerificationResponse.message || 'Otp Verified Successfully',
            timer: 1000
          });
          fetchCustomerDetailById(customerData?.id);
          setTabValue('1');
        }else{
          setIsLoading(false);
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: otpVerificationResponse.message || 'Otp Verification Failed',
            timer: 1000
          })
        }
      }
      console.log('Submitted OTP:', data.otp);
      
    } catch (error) {
      console.error('OTP Verification failed:', error);
      // toast.error('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };
  const [fetchingOtp,setFetchingOtp] = useState(false);
  const handleAutoFillOtp = async () =>{
      setFetchingOtp(true);
      const response = await CustomerService.fetchCustomerVerificationOtp(otrDetails.mobileNumber.replace(/\D/g, ''));
      if(response.success == '1'){
        // setValue('otp', response.data.otp);
        const otp = String(response.data.otp).padStart(6, '0'); // ensure 6 digits
        handleOtpChange(otp);
        setFetchingOtp(false);
      }
    }

  return (
    <Paper sx={{  p: '2%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ textAlign: 'left', width: '85%', my: '2%' }}>
        <Typography sx={{fontWeight:600,fontSize:'1rem',textAlign:'center'}}>OTP Verification</Typography>
        <Typography sx={{fontWeight:600,fontSize:'0.85rem',textAlign:'center'}}>Enter the OTP you received to <Box component={'span'} sx={{color:'#1976d2',fontWeight:400}}>+91-{otrDetails?.mobileNumber?.replace(/\D/g, '') || 'XXXXXXXXXX'}</Box></Typography>
        <Typography sx={{fontWeight:600,fontSize:'0.85rem'}}>
          
        </Typography>
      </Box>

      <Box 
        component="form" 
        noValidate 
        sx={{ mt: 1 }} 
        width={'100%'} 
        onSubmit={handleFormSubmit(onSubmit)}
      >
        <Box sx={{display:"flex",justifyContent:"space-evenly",alignItems:'center'}}>
          <Controller
            name="otp"
            control={control}
            rules={{
              required: 'OTP is required',
              minLength: {
                value: 6,
                message: 'OTP must be 6 digits'
              },
              maxLength: {
                value: 6,
                message: 'OTP must be 6 digits'
              }
            }}
            render={({ field }) => (
              <MuiOtpInput
                sx={{ 
                  width: otpBoxSize, 
                  margin: 'auto',
                  '& .MuiOutlinedInput-root': {
                    height: '2.1rem',
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#f44336',
                      },
                    },
                  },
                }}
                length={6}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  handleOtpChange(value);
                }}
                gap={2}
                TextFieldsProps={{
                  error: !!errors.otp,
                  placeholder: '-'
                }}
              />
            )}
          />
          <PermissionGuard feature={'customer-otp'} permission='canView'>
            <Button disabled={fetchingOtp} size='small' variant='contained' color='success' sx={{textTransform:'capitalize'}} onClick={handleAutoFillOtp}>Auto Fill</Button>
          </PermissionGuard>
        </Box>

        {errors.otp && (
          <Typography 
            variant="caption" 
            color="error" 
            sx={{ display: 'block', textAlign: 'center', mt: 1 }}
          >
            {errors.otp.message}
          </Typography>
        )}

        <Box sx={{ my: '2%', textAlign: 'center' }}>
          <Button 
            disabled={!isValid || watchedOtp?.length !== 6 || isLoading || isOtpExpired} 
            variant="contained" 
            type="submit"
            sx={{ 
              minWidth: '120px',
              backgroundColor: isOtpExpired ? '#f44336' : undefined,
              '&.Mui-disabled': {
                backgroundColor: isOtpExpired ? '#ffcdd2' : undefined,
                color: isOtpExpired ? '#d32f2f' : undefined
              }
            }}
          >
            {isLoading ? 'Verifying...' : 
             isOtpExpired ? 'OTP Expired' : 'Verify OTP'}
          </Button>
          <Button variant='contained' color='warning' sx={{ml:1,textTransform:'capitalize'}} onClick={()=>setTabValue('1')}>
            {/* Verify Later & Skip Now */}
            Skip Verification (Verify Later)
          </Button>
        </Box>

        {/* Timer Display or Expiration Message */}
        {isOtpExpired ? (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#f44336',
                fontWeight: 'bold',
                mb: 1
              }}
            >
              ⏰ OTP Expired!
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666',
                mb: 2,
                lineHeight: 1.5
              }}
            >
              Your OTP has expired ...  Please request a new OTP.
            </Typography>
          </Box>
        ) : (
          <Typography textAlign={'center'} sx={{ mb: 1 }}>
            OTP will expire in:{' '}
            <Box 
              component={'span'} 
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 'bold',
                color: getTimerColor(resendTimer),
                transition: 'color 0.3s ease'
              }}
            >
              {formatTime(resendTimer)}
            </Box>
          </Typography>
        )}

        <Box sx={{ my: '5%', display: 'flex', justifyContent: 'space-between', padding: '0 5%' }}>
          <Button 
            onClick={handleOtpResend} 
            disabled={resendTimer > 0 || isLoading}
            variant={resendTimer > 0 ? "outlined" : "contained"}
            color={isOtpExpired ? "success" : (resendTimer > 0 ? "inherit" : "primary")}
            sx={{
              textTransform: 'none',
              transition: 'all 0.3s ease',
              opacity: resendTimer > 0 ? 0.6 : 1,
              // fontWeight: isOtpExpired ? 'bold' : 'normal',
              animation: isOtpExpired ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.05)' },
                '100%': { transform: 'scale(1)' }
              }
            }}
          >
            {isLoading ? 'Sending...' : 
             isOtpExpired ? 'Get New OTP' : 'Resend OTP'}
            {resendTimer > 0 && !isOtpExpired && (
              <Box component="span" sx={{ ml: 1, fontSize: '0.8rem' }}>
                ({formatTime(resendTimer)})
              </Box>
            )}
          </Button>

          <Typography paragraph variant="body1">
            <span>Click Here! </span>
            <Box 
              component={'span'} 
              onClick={() => setStep(0)} 
              sx={{ 
                textDecoration: 'none', 
                cursor: 'pointer', 
                color: '#1976d2',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Change mobile number
            </Box>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default OtpVerification;